/**
 * Вкладка «Выделить» панели расширения в Bitrix24: сотрудник КЦ сам берёт себе работу — лиды и
 * сделки, по 15 за нажатие; число в поле у кнопки правится (разметка владельца 21.10.26).
 * Правила отбора — те же, что у руководителя на «Распределении»:
 *   Лиды                          у вас 4 из 15
 *     По часовому поясу     12      [12] [Взять]   ← только до 10:00 МСК
 *       Новосибирск +4 ч: 9 · Омск +3 ч: 3 · Екатеринбург с 08:00: 20
 *     Новые                 71      [15] [Взять]
 *     Повторные прозвоны    38      [15] [Взять]
 *   Сделки                        у вас 22 из 50
 *     Обзвон               112      [15] [Взять]
 *     Перезвон              17      [15] [Взять]
 *     Будущий · в работе    64      [15] [Взять]
 *     Будущий · думают      31      [13] [Взять]
 * Брать может сотрудник КЦ, сегодня выходивший на линию; остальным — числа и причина сверху.
 * Потолок: лиды — пока необработанных на сотруднике меньше 15 и не больше 15 за раз, сделки —
 * до 50 на руках.
 * **Кнопка пишет в боевой Bitrix** (`POST /api/ext/take`). То же окно — кнопка «Взять» вкладки
 * «Обзвон» в «Чатах» (`components/chat/Dial.tsx`).
 * Спека `docs/specs/2026-10-08-ext-bitrix-design.md`; «По часовому поясу» — новые лиды, у клиента
 * которых по региону номера уже 10–21 (смена с 7:00 МСК, владелец 22.10.26) —
 * `docs/specs/2026-10-22-take-leads-zone-design.md`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { log } from "@/ext/bridge";
import { apiFetch } from "@/lib/api";
import { btn } from "@/lib/panelBtn";

type Kind = "leads" | "leads_local" | "leads_redial" | "deals" | "deals_redial" | "deals_repeat";
type Summary = {
  can: boolean;
  why: string;
  step: number;
  leads: { held: number; limit: number; unassigned: number; redial: number; local?: Local };
  deals: { held: number; limit: number; queue: number; redial: number; repeat: Record<string, number> };
};
/** Пояс: `shift` — часов от Москвы, `from` — с какого часа по Москве звоним, `now` — уже можно. */
type Zone = { shift: number; city: string; count: number; from: string; now: boolean };
/** `morning` — по Москве рабочие часы ещё не начались: только тогда строка и показывается. */
type Local = { morning: boolean; count: number; zones: Zone[] };
type Taken = {
  kind: Kind;
  requested: number;
  changed: number;
  skipped?: number;
  errors: { error: string }[];
};
/** `give` — число в поле по умолчанию, `max` — больше выдать нельзя. */
type Row = { id: string; kind: Kind; stage?: "3" | "5"; label: string; hint: string; count: number; give: number; max: number; sub?: string };

/** Числа считаются по CRM и на сервере живут минуту — чаще спрашивать незачем. */
const ОБНОВЛЯТЬ = 60_000;
const note = "text-[12.5px] text-fg-muted";

const plural = (n: number, one: string, few: string, many: string) =>
  n % 10 === 1 && n % 100 !== 11 ? one : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? few : many;

function rows(s: Summary): { leads: Row[]; deals: Row[] } {
  // Лиды: потолок закрывает кнопку целиком, за раз — не больше шага; сделки добираем до предела.
  const lead = (count: number) => (s.leads.held < s.leads.limit ? Math.min(s.step, count) : 0);
  const deal = (count: number) => Math.max(0, Math.min(count, s.deals.limit - s.deals.held));
  const row = (r: Omit<Row, "give" | "max">, max: number): Row => ({ ...r, max, give: Math.min(s.step, max) });
  const local = s.leads.local?.morning ? s.leads.local : null;
  return {
    leads: [
      ...(local
        ? [row({ id: "leads_local", kind: "leads_local", label: "По часовому поясу", hint: "Новые лиды, у клиента которых по региону номера уже 10–21", count: local.count, sub: zonesText(local.zones) }, lead(local.count))]
        : []),
      row({ id: "leads", kind: "leads", label: "Новые", hint: "Не распределённые лиды с телефоном", count: s.leads.unassigned }, lead(s.leads.unassigned)),
      row({ id: "leads_redial", kind: "leads_redial", label: "Повторные прозвоны", hint: "Лиды «В работе», по которым давно не звонили", count: s.leads.redial }, lead(s.leads.redial)),
    ],
    deals: [
      row({ id: "deals", kind: "deals", label: "Обзвон", hint: "Сделки сегодняшнего обзвона без ответственного", count: s.deals.queue }, deal(s.deals.queue)),
      row({ id: "deals_redial", kind: "deals_redial", label: "Перезвон", hint: "Утром не дозвонились, звонок больше часа назад", count: s.deals.redial }, deal(s.deals.redial)),
      row({ id: "deals_repeat_3", kind: "deals_repeat", stage: "3", label: "Будущий · в работе", hint: "Сделки «В работе»: не звонили или недозвон", count: s.deals.repeat["3"] ?? 0 }, deal(s.deals.repeat["3"] ?? 0)),
      row({ id: "deals_repeat_5", kind: "deals_repeat", stage: "5", label: "Будущий · думают", hint: "Сделки «Думают»: не звонили или недозвон", count: s.deals.repeat["5"] ?? 0 }, deal(s.deals.repeat["5"] ?? 0)),
    ],
  };
}

/** «Новосибирск +4 ч: 9 · Екатеринбург с 08:00: 20» — московские не пишем: им звонят с 10:00. */
const zonesText = (zones: Zone[]) =>
  zones
    .filter((z) => z.shift !== 0 && z.count)
    .map((z) => `${z.city} ${z.now ? `${z.shift > 0 ? "+" : ""}${z.shift} ч` : `с ${z.from}`}: ${z.count}`)
    .join(" · ") || "Регион номера ни у кого не восточнее Москвы";

/** Число из поля: пустое или мусор — по умолчанию, иначе в пределах 1…max. */
const howMany = (row: Row, typed: string | undefined) => {
  const n = Math.floor(Number(typed));
  return typed === undefined || typed.trim() === "" || !Number.isFinite(n) ? row.give : Math.max(1, Math.min(row.max, n));
};

function told(r: Taken): { text: string; ok: boolean } {
  const what = r.kind.startsWith("leads") ? (["лид", "лида", "лидов"] as const) : (["сделка", "сделки", "сделок"] as const);
  const parts = [r.changed ? `Выдано: ${r.changed} ${plural(r.changed, what[0], what[1], what[2])}` : "Ничего не выдано — брать было нечего"];
  if (r.skipped) parts.push(`${r.skipped} уже заняты`);
  if (r.errors.length) parts.push(`ошибок Bitrix: ${r.errors.length} (${r.errors[0]?.error ?? ""})`);
  return { text: parts.join(" · "), ok: r.changed > 0 && !r.errors.length };
}

/** `active` — вкладка на экране: только тогда числа обновляются сами; `onTaken` — после выдачи. */
export function Take({ active, onTaken }: { active: boolean; onTaken?: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["ext-take"],
    queryFn: () => apiFetch<Summary>("/api/ext/take"),
    enabled: active,
    refetchInterval: ОБНОВЛЯТЬ,
    staleTime: ОБНОВЛЯТЬ / 2,
  });
  const [done, setDone] = useState<{ text: string; ok: boolean } | null>(null);
  // Что сотрудник набрал в поле строки; нет — число по умолчанию.
  const [typed, setTyped] = useState<Record<string, string>>({});
  const take = useMutation({
    mutationFn: ({ row, count }: { row: Row; count: number }) =>
      apiFetch<Taken>("/api/ext/take", { method: "POST", body: JSON.stringify({ kind: row.kind, stage: row.stage ?? null, count }) }),
    onMutate: () => setDone(null),
    onSuccess: (r) => {
      log("take", { kind: r.kind, changed: r.changed, errors: r.errors.length });
      setDone(told(r));
      onTaken?.();
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ["ext-take"] }),
  });

  if (q.isPending) return <p className={note}>Загрузка — считаю очереди…</p>;
  if (q.isError) return <p className="text-[12.5px] text-err">Очереди не загрузились: {q.error.message}</p>;
  const s = q.data;
  const { leads, deals } = rows(s);
  const group = (title: string, held: number, limit: number, list: Row[]) => (
    <section className="flex flex-col" data-annot={`выделить ${title.toLowerCase()}`}>
      <div className="flex items-baseline justify-between gap-2 border-b border-border pb-1">
        <h2 className="text-[13px] font-medium">{title}</h2>
        <span className={`text-[12px] tabular-nums ${held >= limit ? "text-warn" : "text-fg-muted"}`} title={held >= limit ? "Потолок: сначала обработайте взятые" : undefined}>
          у вас {held} из {limit}
        </span>
      </div>
      {list.map((row) => {
        const busy = take.isPending && take.variables?.row.id === row.id;
        const why = !s.can ? s.why : !row.count ? "Брать нечего" : !row.max ? "Потолок: сначала обработайте взятые" : row.hint;
        const off = !s.can || !row.max || take.isPending;
        const go = () => {
          const count = howMany(row, typed[row.id]);
          setTyped((t) => ({ ...t, [row.id]: String(count) }));
          take.mutate({ row, count });
        };
        return (
          <div key={row.id} className="flex flex-wrap items-center gap-x-2 border-b border-border py-0.5 last:border-b-0" data-annot={`выделить ${row.id}`}>
            <span className="min-w-0 flex-1 truncate text-[12.5px]" title={row.hint}>{row.label}</span>
            <span className="w-9 shrink-0 text-right text-[12.5px] tabular-nums text-fg-muted">{row.count}</span>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={row.max || 1}
              aria-label={`Сколько взять: ${row.label}`}
              title={row.max ? `Сколько взять: от 1 до ${row.max}` : why}
              className="h-7 w-11 shrink-0 rounded-[var(--radius-ui)] border border-border bg-bg px-1 text-right text-[12.5px] tabular-nums disabled:opacity-50"
              disabled={off}
              value={typed[row.id] ?? (row.give || s.step)}
              onChange={(e) => setTyped((t) => ({ ...t, [row.id]: e.target.value }))}
              onKeyDown={(e) => e.key === "Enter" && !off && go()}
            />
            <button type="button" className={`${btn.outline} w-[4.5rem] shrink-0 px-1`} disabled={off} title={why} onClick={go}>
              {busy ? "Выдаю…" : "Взять"}
            </button>
            {row.sub && <p className="w-full pb-0.5 text-[11.5px] leading-snug text-fg-muted">{row.sub}</p>}
          </div>
        );
      })}
    </section>
  );
  return (
    <div className="flex flex-col gap-2">
      {!s.can && <p role="note" className="rounded-[var(--radius-ui)] bg-warn-bg px-2 py-1.5 text-[12.5px] leading-snug text-fg">{s.why}</p>}
      {group("Лиды", s.leads.held, s.leads.limit, leads)}
      {group("Сделки", s.deals.held, s.deals.limit, deals)}
      {done && (
        <p role="status" className={`rounded-[var(--radius-ui)] px-2 py-1.5 text-[12.5px] leading-snug ${done.ok ? "bg-ok-bg text-ok" : "bg-warn-bg text-fg"}`}>
          {done.text}
        </p>
      )}
      {take.isError && <p role="alert" className="rounded-[var(--radius-ui)] bg-err-bg px-2 py-1.5 text-[12.5px] leading-snug text-err">{take.error.message}</p>}
    </div>
  );
}
